import { useState } from 'react';
import { ArrowLeftOutlined, CheckCircleOutlined, CloudOutlined, CopyOutlined, DeleteOutlined, LaptopOutlined, ReloadOutlined, SafetyCertificateOutlined, StopOutlined, WalletOutlined } from '@ant-design/icons';
import { App, Avatar, Button, Descriptions, Empty, Popconfirm, Space, Tabs, Tag, Tooltip } from 'antd';
import { api } from '../api';
import { ActiveTag, DataTable, ErrorNotice, Panel, PlanTag, StatCard, SubscriptionTag } from '../components';
import { actionLabel, avatarColor, formatDate, formatDateTime, formatRupees, initials, subscriptionState, timeAgo, todayIso, useLoad } from '../lib';
import { DealerPanel, money, useGrants, WalletPanel } from './accountPanels';

/**
 * ADMIN › Accounts › one account (owner 2026-11-01): who it is, its licence (activated for life or not, renewal end),
 * and every section kept for it on tabs: details, subscriptions, payments, computers, wallet, dealer and activity.
 * Activate, Renew and Enable / Disable are on the header.
 */
const TABS = ['overview', 'subscriptions', 'payments', 'computers', 'wallet', 'dealer', 'activity'];
const PAYMENT_TAGS = { paid: 'success', created: 'processing', failed: 'error' };

function Detail({ children }) {
    return children ? <>{children}</> : <span className="cell-sub">—</span>;
}

export function AccountProfilePage({ id, go }) {
    const { message } = App.useApp();
    const loaded = useLoad(() => api(`/v1/admin/accounts/${id}`), [id]);
    const [tab, setTab] = useState(() => {
        const asked = new URLSearchParams(window.location.search).get('tab');
        return TABS.includes(asked) ? asked : 'overview';
    });
    const grants = useGrants(loaded.reload);
    const data = loaded.data;
    const account = data?.account;
    const subscription = data?.subscription;
    const signedIn = (data?.devices ?? []).filter((row) => !row.removedAt);

    function openTab(next) {
        setTab(next);
        // Kept in the address so a reload or a shared link opens the same tab.
        const url = new URL(window.location.href);
        url.searchParams.set('tab', next);
        window.history.replaceState(null, '', url);
    }
    async function toggle() {
        try {
            await api(`/v1/admin/accounts/${account.id}/${account.disabledAt ? 'enable' : 'disable'}`, { method: 'POST' });
            message.success(account.disabledAt ? `${account.name} can sign in again` : `${account.name} is disabled`);
            await loaded.reload();
        }
        catch (error) {
            message.error(error instanceof Error ? error.message : 'Could not change the account');
        }
    }
    async function removeComputer(row) {
        try {
            await api(`/v1/admin/devices/${row.id}/remove`, { method: 'POST' });
            message.success(`${row.name} is signed out. The account can sign in on another computer.`);
            await loaded.reload();
        }
        catch (error) {
            message.error(error instanceof Error ? error.message : 'Could not remove the computer');
        }
    }
    async function copy(text, what) {
        try {
            await navigator.clipboard.writeText(text);
            message.success(`${what} copied`);
        }
        catch {
            message.error(`Could not copy the ${what.toLowerCase()}`);
        }
    }

    const back = (<Button type="link" icon={<ArrowLeftOutlined />} onClick={() => go('accounts')} style={{ paddingLeft: 0 }}>
      Accounts
    </Button>);
    if (loaded.error && !data) {
        return (<>
          {back}
          <ErrorNotice error={loaded.error}/>
        </>);
    }

    // Cloud services run while any period covers today (a renewal bought ahead starts after the running one), until
    // the latest period ends.
    const today = todayIso();
    const running = (data?.subscriptions ?? []).some((row) => row.validFrom <= today && today <= row.validUntil);
    const renewalState = subscription ? subscriptionState(running ? { ...subscription, validFrom: today } : subscription) : null;
    return (<>
      {back}
      <section className="panel profile-head" aria-busy={!data || undefined}>
        <Avatar size={64} style={{ background: avatarColor(account?.name ?? id), fontWeight: 600, fontSize: 24, flex: 'none' }}>
          {account ? initials(account.name) : ''}
        </Avatar>
        <div className="profile-id">
          <h1>{account?.name ?? 'Loading…'}</h1>
          {account && (<div className="profile-meta">
              {account.tradeName && <span>{account.tradeName}</span>}
              {account.mobile && (<span className="num">
                  {account.mobile}
                  <Tooltip title="Copy mobile"><Button type="text" size="small" icon={<CopyOutlined />} onClick={() => copy(account.mobile, 'Mobile')} aria-label="Copy mobile"/></Tooltip>
                </span>)}
              {account.email && <span>{account.email}</span>}
              <span>Joined {formatDate(account.createdAt)}</span>
            </div>)}
          {data && (<Space size={6} wrap style={{ marginTop: 10 }}>
              <ActiveTag active={!account.disabledAt}/>
              {data.activated ? <Tag color="green" variant="filled" icon={<SafetyCertificateOutlined />}>Activated for life</Tag> : <Tag variant="filled">Not activated</Tag>}
              {data.dealer?.active && <Tag color="purple" variant="filled">Dealer</Tag>}
            </Space>)}
        </div>
        {data && (<div className="profile-actions">
            {!account.disabledAt && !data.activated && (<Button type="primary" icon={<SafetyCertificateOutlined />} onClick={() => grants.activate(account)}>
                Activate
              </Button>)}
            {!account.disabledAt && data.activated && (<Button type="primary" icon={<ReloadOutlined />} onClick={() => grants.renew(account)}>
                Renew
              </Button>)}
            {account.disabledAt ? (<Button icon={<CheckCircleOutlined />} onClick={toggle}>
                Enable
              </Button>) : (<Popconfirm title="Disable this account?" description={`${account.name} will not be able to sign in, in the account panel or the desktop app, until it is enabled again.`} okText="Disable" okButtonProps={{ danger: true }} onConfirm={toggle}>
                <Button danger icon={<StopOutlined />}>
                  Disable
                </Button>
              </Popconfirm>)}
          </div>)}
      </section>

      <div className="stats">
        <StatCard label="Licence" tone="green" icon={<SafetyCertificateOutlined />} loading={!data} value={data?.activated ? 'Activated' : 'Not activated'} foot={data?.activated ? 'Software unlocked for life' : 'Activate to unlock the software'}/>
        <StatCard label="Cloud services" tone="indigo" icon={<CloudOutlined />} loading={!data} value={subscription ? formatDate(subscription.validUntil) : '—'} foot={renewalState ? <SubscriptionTag state={renewalState}/> : 'No renewal yet'}/>
        <StatCard label="Computer" tone="sky" icon={<LaptopOutlined />} loading={!data} value={<span className="stat-value-text" title={signedIn[0]?.name}>{signedIn[0]?.name ?? 'None'}</span>} foot={signedIn[0] ? `Seen ${timeAgo(signedIn[0].lastSeenAt ?? signedIn[0].activatedAt)}` : 'One computer at a time'}/>
        <StatCard label="Wallet" tone="amber" icon={<WalletOutlined />} loading={!data} value={money(data?.wallet?.availablePaise)} foot={data?.dealer?.active ? `Dealer credits · A ${data.dealer.balances.activation} · R ${data.dealer.balances.renewal}` : 'Available balance'}/>
      </div>

      {data && (<Panel className="profile-tabs">
          <Tabs activeKey={tab} onChange={openTab} items={[
                {
                    key: 'overview',
                    label: 'Overview',
                    children: (<div className="profile-overview">
                      <Descriptions title="Account" column={{ xs: 1, md: 2 }} size="small" bordered items={[
                            { key: 'name', label: 'Full name', children: account.name },
                            { key: 'trade', label: 'Trade name', children: <Detail>{account.tradeName}</Detail> },
                            { key: 'mobile', label: 'Mobile', children: <Detail>{account.mobile}</Detail> },
                            { key: 'email', label: 'Email', children: <Detail>{account.email && <>{account.email}{account.emailVerifiedAt ? <Tag color="green" style={{ marginLeft: 8 }}>Verified</Tag> : null}</>}</Detail> },
                            { key: 'created', label: 'Created', children: formatDateTime(account.createdAt) },
                            { key: 'status', label: 'Status', children: account.disabledAt ? `Disabled on ${formatDate(account.disabledAt)}` : 'Active' }
                        ]}/>
                      <Descriptions title="Billing details" column={{ xs: 1, md: 2 }} size="small" bordered items={[
                            { key: 'bname', label: 'Billing name', children: <Detail>{account.billing?.name}</Detail> },
                            { key: 'gstin', label: 'GSTIN', children: <Detail>{account.billing?.gstin && <span className="mono">{account.billing.gstin}</span>}</Detail> },
                            { key: 'state', label: 'State', children: <Detail>{account.billing?.state}</Detail> },
                            { key: 'bemail', label: 'Invoice email', children: <Detail>{account.billing?.email}</Detail> },
                            { key: 'address', label: 'Address', span: 2, children: <Detail>{account.billing?.address}</Detail> }
                        ]}/>
                      <Descriptions title="Licence" column={{ xs: 1, md: 2 }} size="small" bordered items={[
                            { key: 'act', label: 'Activation', children: data.activated ? 'Activated: software unlocked for life' : 'Not activated' },
                            { key: 'plan', label: 'Current plan', children: subscription ? <PlanTag plan={subscription.plan} name={subscription.planName}/> : <Detail /> },
                            { key: 'until', label: 'Cloud services till', children: subscription ? <Space>{formatDate(subscription.validUntil)}<SubscriptionTag state={renewalState}/></Space> : <Detail /> },
                            { key: 'computer', label: 'Signed in on', children: signedIn.length ? signedIn.map((row) => row.name).join(', ') : 'No computer' }
                        ]}/>
                    </div>)
                },
                {
                    key: 'subscriptions',
                    label: `Subscriptions (${data.subscriptions.length})`,
                    children: (<DataTable rowKey="id" loading={false} dataSource={data.subscriptions} pagination={data.subscriptions.length > 10 ? { pageSize: 10 } : false} locale={{ emptyText: 'No subscriptions yet.' }} scroll={{ x: 760 }} columns={[
                            { title: 'Plan', render: (_v, row) => <PlanTag plan={row.plan} name={row.planName}/> },
                            { title: 'Type', width: 110, render: (_v, row) => (row.kind === 'trial' ? 'Trial' : 'Plan') },
                            { title: 'From', width: 130, render: (_v, row) => formatDate(row.validFrom) },
                            { title: 'Till', width: 130, render: (_v, row) => formatDate(row.validUntil) },
                            { title: 'Status', width: 150, render: (_v, row) => <SubscriptionTag state={subscriptionState(row)}/> },
                            { title: 'Issued', width: 170, render: (_v, row) => formatDateTime(row.createdAt) }
                        ]}/>)
                },
                {
                    key: 'payments',
                    label: `Payments (${data.payments.length})`,
                    children: (<DataTable rowKey="id" loading={false} dataSource={data.payments} pagination={data.payments.length > 10 ? { pageSize: 10 } : false} locale={{ emptyText: 'No payments yet.' }} scroll={{ x: 820 }} columns={[
                            { title: 'When', width: 170, render: (_v, row) => formatDateTime(row.paidAt ?? row.createdAt) },
                            { title: 'Plan', render: (_v, row) => <span>{row.planName}{row.planKind && <span className="cell-sub">{row.planKind === 'renewal' ? 'Renewal' : 'Activation'}</span>}</span> },
                            { title: 'Amount', width: 130, align: 'right', render: (_v, row) => <span className="num">{formatRupees(row.amountPaise)}</span> },
                            { title: 'Status', width: 110, render: (_v, row) => <Tag color={PAYMENT_TAGS[row.status]} variant="filled" style={{ textTransform: 'capitalize' }}>{row.status}</Tag> },
                            { title: 'Invoice', width: 150, render: (_v, row) => (row.invoiceNumber ? <span className="mono">{row.invoiceNumber}</span> : <span className="cell-sub">{row.failureReason || '—'}</span>) },
                            { title: 'Period', width: 210, render: (_v, row) => (row.validFrom ? `${formatDate(row.validFrom)} – ${formatDate(row.validUntil)}` : <span className="cell-sub">—</span>) }
                        ]}/>)
                },
                {
                    key: 'computers',
                    label: `Computers (${signedIn.length})`,
                    children: (<>
                      <p className="cell-sub" style={{ marginTop: 0 }}>An account is signed in on one computer at a time. Removing it signs that computer out, so the account can sign in elsewhere.</p>
                      <DataTable rowKey="id" loading={false} dataSource={data.devices} pagination={data.devices.length > 10 ? { pageSize: 10 } : false} locale={{ emptyText: <Empty description="No computer has signed in yet." image={Empty.PRESENTED_IMAGE_SIMPLE}/> }} scroll={{ x: 820 }} columns={[
                            { title: 'Computer', render: (_v, row) => <span>{row.name}<span className="cell-sub">{row.platform} · v{row.appVersion}</span></span> },
                            { title: 'Signed in', width: 170, render: (_v, row) => formatDateTime(row.activatedAt) },
                            { title: 'Last seen', width: 150, render: (_v, row) => (row.lastSeenAt ? timeAgo(row.lastSeenAt) : '—') },
                            { title: 'Status', width: 170, render: (_v, row) => (row.removedAt ? <Tag variant="filled">Signed out {formatDate(row.removedAt)}</Tag> : <Tag color="success" variant="filled">Signed in</Tag>) },
                            {
                                title: '',
                                width: 110,
                                align: 'right',
                                render: (_v, row) => !row.removedAt && (<Popconfirm title={`Sign out ${row.name}?`} description="That computer stops working with this account until it signs in again." okText="Remove" okButtonProps={{ danger: true }} onConfirm={() => removeComputer(row)}>
                                    <Button size="small" danger icon={<DeleteOutlined />}>Remove</Button>
                                  </Popconfirm>)
                            }
                        ]}/>
                    </>)
                },
                { key: 'wallet', label: 'Wallet', children: <WalletPanel account={account} onChanged={loaded.reload}/> },
                { key: 'dealer', label: data.dealer?.active ? 'Dealer' : 'Dealer (off)', children: <DealerPanel account={account} onChanged={loaded.reload}/> },
                {
                    key: 'activity',
                    label: 'Activity',
                    children: (<DataTable rowKey="id" loading={false} dataSource={data.audit} pagination={data.audit.length > 15 ? { pageSize: 15 } : false} locale={{ emptyText: 'No activity yet.' }} scroll={{ x: 700 }} columns={[
                            { title: 'When', width: 180, render: (_v, row) => formatDateTime(row.at) },
                            { title: 'What', render: (_v, row) => actionLabel(row.action) },
                            { title: 'Details', render: (_v, row) => <span className="cell-sub">{describe(row.meta)}</span> }
                        ]}/>)
                }
            ]}/>
        </Panel>)}
      {grants.dialog}
    </>);
}

/** The useful parts of an activity row's details, in a line. */
function describe(meta) {
    if (!meta || typeof meta !== 'object')
        return '';
    const parts = [];
    if (meta.by) parts.push(`by ${meta.by}`);
    if (meta.actor) parts.push(`by ${meta.actor}`);
    if (meta.plan) parts.push(`plan ${meta.plan}`);
    if (meta.name) parts.push(meta.name);
    if (meta.years) parts.push(`${meta.years} ${meta.years === 1 ? 'year' : 'years'}`);
    if (meta.units) parts.push(`${meta.units > 0 ? '+' : ''}${meta.units} ${meta.kind ?? ''} credits`.trim());
    if (meta.validUntil) parts.push(`till ${formatDate(meta.validUntil)}`);
    if (meta.invoice) parts.push(meta.invoice);
    if (meta.amountPaise) parts.push(formatRupees(meta.amountPaise));
    return parts.join(' · ');
}
